import { Auth } from '@aws-amplify/auth';
import { Link, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import React, { useContext, useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import ReactMarkdown from 'react-markdown';

import { authentication } from '../../utils/enums';
import Name from '../shared/Name';
// TODO Removed for 1.0.0, replace once Spanish translations are finished.
// import LanguageSelector from '../shared/LanguageSelector';
import Email from '../shared/Email';
import Phone from '../shared/Phone';
import MultiDigitInput from '../shared/MultiDigitInput';
import NextButton from '../shared/NextButton';
import Password from '../shared/Password';
import RenderIf from '../renderIf/RenderIf';
import { FormContext, ShipyardContext } from '../Context';

import './Signup.scss';
import VerificationCode from './VerificationCode';

function Signup() {
    const { userSettings } = useContext(ShipyardContext);
    const [t] = useTranslation();

    const validator = (ele) => {
        return ele.attributes['aria-invalid'].value !== 'true';
    };

    const [code, setCode] = React.useState('');
    const [step, setStep] = React.useState(1);
    const [validate, setValidate] = useState(false);
    const [validated, setValidated] = useState(false);
    const [blankEmail, setBlankEmail] = useState(true);
    const [blankPassword, setBlankPassword] = useState(true);
    const [blankFirstName, setBlankFirstName] = useState(true);
    const [blankLastName, setBlankLastName] = useState(true);
    const [blankMobile, setBlankMobile] = useState(true);
    const [blankRepeatPassword, setBlankRepeatPassword] = useState(true);
    const [matchFailure, setMatchFailure] = useState(false);
    const [doingAjax, setDoingAjax] = React.useState(false);
    const [collectedData, setCollectedData] = useState();
    const [user, setUser] = useState({});
    const [inputEmail, setInputEmail] = React.useState('');
    const [inputPassword, setInputPassword] = React.useState('');
    const [verificationCodeStep, setVerificationCodeStep] = React.useState(false);
    const [mfaChallengeType, setMfaChallengeType] = React.useState('');

    const history = useHistory();

    const emailRef = useRef();
    const passwordRef = useRef();
    const firstNameRef = useRef();
    const lastNameRef = useRef();
    const mobileRef = useRef();
    const repeatPasswordRef = useRef();

    const handleInitialSubmit = async (e) => {
        e.preventDefault();
        setValidate(true);
    };

    const postAuth = async () => {
        history.push('/applications');
    };

    const handleVerificationSubmit = async (code) => {
        try {
            await Auth.confirmSignIn(user, code, mfaChallengeType);
            await postAuth();
        } catch (err) {
            if (err.code === 'CodeMismatchException') {
                toast.error(t('Authentication.VerificationCodeIncorrect'));
                return;
            }
            if (err.message.toLowerCase().includes('session is expired')) {
                toast.error(t('General.AutoLogout'));
                setVerificationCodeStep(false);
                return;
            }
            console.error('Encountered unexpected error while confirming MFA challenge.');
            console.error(err);
            return;
        }
    };

    const handleVerificationCodeCancel = () => {
        history.push('/signin');
    };

    const handleVerificationResend = async () => {
        try {
            let user = await Auth.signIn(inputEmail, inputPassword);
            setUser(user);
            toast.success(t(mfaChallengeType === authentication.challenge.SMS ? 'Cognito.MFAResendSuccess' : 'Cognito.ResendSuccess'));
        } catch (e) {
            toast.error(t('Cognito.ResendError'));
        }
    };

    const handleVerifySubmit = async (e) => {
        e.preventDefault();
        try {
            setDoingAjax(true);
            setInputEmail(collectedData?.email);
            setInputPassword(collectedData?.password);
            await Auth.confirmSignUp(collectedData?.email, code);
            let signupUser = await Auth.signIn(collectedData?.email, collectedData?.password);
            setDoingAjax(false);
            if (!signupUser.challengeName) {
                await postAuth();
                return;
            }
            switch (signupUser.challengeName) {
                case authentication.challenge.SMS:
                    setUser(signupUser);
                    setMfaChallengeType(authentication.challenge.SMS);
                    setVerificationCodeStep(true);
                    break;

                default:
                    console.error(`Unknown challenge name: ${signupUser.challengeName}`);
                    toast.error(t('General.UnknownError'));
                    return;
            }
        } catch (verifyErr) {
            console.error('Shipyard: Error verifying sign up', verifyErr);
            setDoingAjax(false);
            if (verifyErr.name === 'CodeMismatchException') {
                toast.error(t('Authentication.VerificationCodeIncorrect'));
            }
            if (verifyErr.name === 'ExpiredCodeException') {
                toast.error(t('Authentication.VerificationCodeExpired'));
                Auth.resendSignUp(collectedData?.email);
                setCode('');
            }
            if (verifyErr.name === 'InvalidLambdaResponseException') {
                toast.error(t('General.UnknownError'));
            }
            if (verifyErr.message && verifyErr.message.indexOf('status is CONFIRMED') > 0) {
                toast.success(t('Authentication.AccountVerified'));
                history.push('/signin');
            }
        }
    };

    useEffect(() => {
        setValidate(false);
        if (validated) {
            const phoneRgx = /^(?:(?:\+)?1|)?[(\-. ]*(\d{3})[)\-. ]*(\d{3})[-. ]*(\d{4})$/;

            setValidated(false);

            let initialSubmit = async () => {
                if (
                    !validator(firstNameRef.current) ||
                    !validator(lastNameRef.current) ||
                    !validator(emailRef.current) ||
                    !validator(mobileRef.current)
                ) {
                    toast.error(t('Authentication.CreateAccount.InvalidInput'));
                    let invalidInputs = document.getElementsByClassName('validated-input invalid');
                    if (invalidInputs.length) invalidInputs[0].querySelector('input').focus();

                    return;
                }

                if (passwordRef?.current?.value !== repeatPasswordRef?.current?.value) {
                    toast.error(t('Authentication.CreateAccount.PasswordsNotMatch'));
                    setMatchFailure(true);
                    return;
                }

                var phoneMatches = mobileRef.current.value.match(phoneRgx);
                var phoneE164 = '+1' + phoneMatches[1] + phoneMatches[2] + phoneMatches[3];

                setDoingAjax(true);

                try {
                    var authResult = await Auth.signUp({
                        username: emailRef.current.value,
                        password: passwordRef.current.value,
                        attributes: {
                            'custom:clientId': `${userSettings.ClientOrgId}`,
                            email: emailRef.current.value,
                            phone_number: phoneE164,
                            given_name: firstNameRef.current.value,
                            family_name: lastNameRef.current.value,
                        },
                    });
                    setDoingAjax(false);
                    if (authResult && authResult.codeDeliveryDetails && authResult.codeDeliveryDetails.DeliveryMedium === 'EMAIL') {
                        setCollectedData({
                            email: emailRef.current.value,
                            password: passwordRef.current.value,
                        });
                        setStep(2);
                        return;
                    }
                    toast.error(t('General.UnknownError'));
                } catch (authErr) {
                    setDoingAjax(false);
                    console.error('Shipyard: error signing up:', authErr);
                    if (authErr.name === 'InvalidPasswordException') {
                        toast.error(t('Authentication.PasswordWeak'));
                        return;
                    }
                    if (authErr.name === 'UsernameExistsException') {
                        toast.error(t('Authentication.CreateAccount.AccountExists'));
                        return;
                    }
                    if (authErr.message && authErr.message.indexOf('SignUp is not permitted') >= 0) {
                        toast.error(t('Authentication.CreateAccount.NotAllowed'));
                        return;
                    }
                    toast.error(t('General.UnknownError'));
                }
            };

            initialSubmit();
        }
    }, [validated, t]);

    return (
        <div id="page-signup">
            <div id="page-signup-foreground">
                <h1>
                    <img
                        alt="Client Header"
                        src={userSettings.ClientHeaderImage || '/pcg-scholar.svg'}
                    />
                </h1>
                <div role="main">
                    <RenderIf isTrue={step === 1}>
                        <form id="form-signup">
                            <FormContext.Provider value={{ triggerValidation: validate, validationFinished: setValidated }}>
                                <h2>{t('Authentication.CreateAccount.Title')}</h2>
                                <p>{t('Authentication.CreateAccount.Instructions')}</p>
                                <Name
                                    ref={firstNameRef}
                                    labelText={t('Authentication.CreateAccount.FirstName')}
                                    placeholder={t('Authentication.CreateAccount.EnterFirstName')}
                                    disabled={doingAjax}
                                    autofocus={true}
                                    onChange={(val) => setBlankFirstName(val.length === 0)}
                                />
                                <Name
                                    ref={lastNameRef}
                                    labelText={t('Authentication.CreateAccount.LastName')}
                                    placeholder={t('Authentication.CreateAccount.EnterLastName')}
                                    disabled={doingAjax}
                                    onChange={(val) => setBlankLastName(val.length === 0)}
                                />
                                <Email
                                    ref={emailRef}
                                    labelText={t('Authentication.CreateAccount.Email')}
                                    placeholder={t('Authentication.CreateAccount.EnterEmail')}
                                    disabled={doingAjax}
                                    onChange={(val) => setBlankEmail(val.length === 0)}
                                />
                                <Phone
                                    ref={mobileRef}
                                    labelText={t('Authentication.CreateAccount.Mobile')}
                                    placeholder={t('Authentication.CreateAccount.EnterMobile')}
                                    type="tel"
                                    disabled={doingAjax}
                                    onChange={(val) => setBlankMobile(val.length === 0)}
                                />
                                {/* 
                      TODO Removed for 1.0.0 Release, replace once Spanish Translations are finished.
                      <LanguageSelector /> 
                  */}
                                <Password
                                    innerRef={passwordRef}
                                    labelText={t('Authentication.Password')}
                                    placeholder={t('Authentication.EnterPassword')}
                                    disabled={doingAjax}
                                    onChange={(val) => setBlankPassword(val.length === 0)}
                                />
                                <Password
                                    innerRef={repeatPasswordRef}
                                    className={!matchFailure ? 'passing' : null}
                                    labelText={t('Authentication.CreateAccount.RepeatPassword')}
                                    placeholder={t('Authentication.CreateAccount.EnterRepeatPassword')}
                                    disabled={doingAjax}
                                    onChange={(val) => {
                                        setBlankRepeatPassword(val.length === 0);
                                        setMatchFailure(false);
                                    }}
                                />
                                {matchFailure ? <div id="match-failure">{t('Authentication.ConfirmPasswordInstructions')}</div> : null}
                                <NextButton
                                    action={handleInitialSubmit}
                                    disabled={
                                        doingAjax ||
                                        blankFirstName ||
                                        blankLastName ||
                                        blankEmail ||
                                        blankMobile ||
                                        blankPassword ||
                                        blankRepeatPassword
                                    }
                                >
                                    {t('Authentication.CreateAccount.SendCode')}
                                </NextButton>
                                <div id="terms">
                                    <ReactMarkdown linkTarget={'_blank'}>
                                        {t('Authentication.CreateAccount.TermsAndConditions')}
                                    </ReactMarkdown>
                                </div>
                                <div id="return-to-login">
                                    {t('Authentication.CreateAccount.AlreadyHave') + ' '}{' '}
                                    <Link to="/signin">{t('Authentication.CreateAccount.LogIn')}</Link>
                                </div>
                            </FormContext.Provider>
                        </form>
                    </RenderIf>
                    <RenderIf isTrue={step === 2}>
                        <RenderIf isTrue={verificationCodeStep}>
                            <VerificationCode
                                callback={handleVerificationSubmit}
                                cancelCallback={handleVerificationCodeCancel}
                                resendCallback={handleVerificationResend}
                            />
                        </RenderIf>
                        <RenderIf isTrue={!verificationCodeStep}>
                            <form
                                id="form-verify"
                                onSubmit={handleVerifySubmit}
                            >
                                <h2>{t('Authentication.CreateAccount.EnterCode')}</h2>
                                <p>{t('Authentication.CreateAccount.CodeInstructions')}</p>
                                <MultiDigitInput
                                    digits="6"
                                    value={code}
                                    onChange={setCode}
                                    label="Verification Code"
                                    autofocus={true}
                                    disabled={doingAjax}
                                />
                                <NextButton
                                    action={handleVerifySubmit}
                                    disabled={doingAjax || code.length < 6}
                                >
                                    {t('Authentication.CreateAccount.CodeSubmit')}
                                </NextButton>
                            </form>
                        </RenderIf>
                    </RenderIf>
                </div>
            </div>
            <div id="page-signup-background"></div>
        </div>
    );
}

export default Signup;
